import { isPlatformBrowser } from '@angular/common';
import { AfterViewChecked, ChangeDetectorRef, Component, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { CoreSelectors } from '@next/core-lib';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { ButtonItem, FilterTypes } from '@next/core-lib/components';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { SiteState } from '@next/core-lib/site';
import { SelectedCustomer, SelectedLocation, UserActions, UserRelationshipType, UserSelectors } from '@next/core-lib/user';
import { WindowServiceInjector, WindowService } from '@next/core-lib/window';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { combineLatestWith, debounceTime, distinctUntilChanged, filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';

import { Environment } from 'src/environment';

import * as OverviewModels from './models';
import { deliveryPeriodsToColumnTypesForGBM, OrderPlanOverviewV2, SalesOrderPeriod, TableColumnsInfo } from './models';
import { OrderplanColumnTypes } from './models';
import { SwitchTranslateTypes, SwitchTranslationService } from './services/switch-translation.service';
import { CropAid, PeriodSystem, PreferredDeliveryPeriods, TreatmentTypes } from '../overview/models';
import { CustomerProgress, CropFieldLinkTypesEnum } from '../shared/models';
import * as OverviewActions from '../store/actions';
import * as OverviewSelectors from '../store/selectors';
import { selectCustomerProgress, selectPeriodSystem } from '../store/selectors';

enum ButtonAction {
  addProduct = 'addProduct',
  editCustomerProductInventory = 'editCustomerProductInventory',
  getStockFromTelersdashboard = 'getStockFromTelersdashboard',
  optimizeOrderplan = 'optimizeOrderplan',
  createOrderReport = 'createOrderReport',
  createCompleteReport = 'createCompleteReport',
  preferedDeliveryPeriods = 'preferedDeliveryPeriods',
  numberOfPeriods = 'numberOfPeriods',
  linkCropFields = 'linkCropFields',
  customerProgressOrderplan = 'customerProgressOrderplan',
  digitalProducts = 'digitalProducts',
}

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    FeatureToggleSwitch: any;
  }
}

@Component({
  selector: 'app-overview-v2',
  templateUrl: './overview-v2.component.html',
  styleUrls: ['./overview-v2.component.scss'],
  standalone: false,
})
export class OverviewV2Component implements OnInit, OnDestroy, AfterViewChecked {
  readonly env = inject<Environment>(EnvironmentInjector);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly windowService = inject<WindowService>(WindowServiceInjector);
  private readonly appInsights = inject<AppInsightsService>(AppInsightsServiceInjector);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly switchTranslation = inject(SwitchTranslationService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private isLoading = false;

  getTranslationKey = SwitchTranslateTypes.getKey;
  loading$ = this.store.pipe(select(CoreSelectors.selectLoading));
  salesOrderPeriods: OverviewModels.SalesOrderPeriod[];
  cropAids: CropAid[];
  packages: OverviewModels.Package[];
  productInventoryChanged: Subject<OverviewModels.ProductInventoryData> = new Subject<OverviewModels.ProductInventoryData>();
  columnsToDisplay: string[] = [];
  dynamicPeriodsColumns: TableColumnsInfo[] = [];
  expandedElement: OrderPlanOverviewV2 | null;
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer));
  customer: SelectedCustomer;
  location$ = this.store.pipe(select(UserSelectors.selectLocation));
  preferredDeliveryPeriods$ = this.store.pipe(select(OverviewSelectors.selectPreferredDeliveryPeriods));

  periodSystem$ = this.store.pipe(select(selectPeriodSystem));
  location: SelectedLocation;
  sprayLicenses$ = this.store.pipe(select(OverviewSelectors.selectActiveSprayLicenses));
  orderplanOptimized$ = this.store.pipe(select(OverviewSelectors.selectOptimizationStatus));
  orderLineStatus = OverviewModels.OrderLineStatus;
  cropYear$ = this.store.pipe(select(OverviewSelectors.getYear));
  defaultCropYear$ = this.store.pipe(select(OverviewSelectors.selectDefaultYear));
  availableCropYears$ = this.store.pipe(select(OverviewSelectors.getAvailableYears));
  cropFields$ = this.store.pipe(select(OverviewSelectors.selectCropFields));
  areAnyCropFieldsLinked = false;
  customerProgress$ = this.store.pipe(select(selectCustomerProgress));
  areAnyCropFieldsNotLinked = false;
  cropYearChanged$ = new Subject<number>();
  clearTableSelections$: Subject<boolean> = new Subject();
  filterConfig = {
    filters: {
      year: {
        label: 'year',
        type: FilterTypes.Select,
        options: this.availableCropYears$.pipe(
          map((availableYears) =>
            availableYears.map((availableYear) => ({
              value: availableYear,
              viewValue: availableYear.toString(),
            })),
          ),
        ),
        value: this.cropYear$,
        defaultValue: this.defaultCropYear$,
        changes: this.cropYearChanged$,
      },
    },
  };
  periodsSystem: PeriodSystem;
  periodSystemEnum = PeriodSystem;

  buttonItems: ButtonItem[] = [];
  inventories: OverviewModels.ProductInventoryData[];
  selectedRows: OverviewModels.OrderPlanOverviewV2[] = [];
  hasOrderPlan = false;
  isTableVisible$ = new BehaviorSubject(false);
  periods: PreferredDeliveryPeriods;
  orderPlanV2Display: OrderPlanOverviewV2[];

  private readonly destroyed$ = new Subject<boolean>();
  ngAfterViewChecked(): void {
    if (isPlatformBrowser(this.platformId)) {
      //Set full width for sticky header
      const stickyWrapper = document.getElementsByClassName('sticky__wrapper') as HTMLCollectionOf<HTMLElement>;
      if (stickyWrapper && stickyWrapper.length > 0) {
        stickyWrapper[0].style.width = '100%';
      }
      const stickyContainer = document.getElementsByClassName('sticky__container') as HTMLCollectionOf<HTMLElement>;
      if (stickyContainer && stickyContainer.length > 0) {
        stickyContainer[0].style.width = '100%';
      }
    }
  }

  ngOnInit() {
    this.loading$.pipe(takeUntil(this.destroyed$)).subscribe((isLoading) => {
      this.isLoading = isLoading;
      const stockAction = this.buttonItems.find((item) => item.key === ButtonAction.getStockFromTelersdashboard);
      if (stockAction && stockAction.disabled !== isLoading) {
        stockAction.disabled = isLoading;
        this.buttonItems = [...this.buttonItems];
      }
    });

    this.productInventoryChanged
      .pipe(debounceTime(1000), distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((inventory) => this.saveCropAidInventory(inventory));

    this.cropYearChanged$.pipe(withLatestFrom(this.cropYear$), takeUntil(this.destroyed$)).subscribe(([newYear, existingYear]) => {
      if (newYear && newYear !== existingYear) {
        this.store.dispatch(OverviewActions.resetYear({ year: newYear }));
      }
    });

    combineLatest([this.location$, this.cropFields$]).subscribe(([location, cropFields]) => {
      if (location && cropFields) {
        this.areAnyCropFieldsLinked = cropFields.filter((c) => c.shipToId === location?.id).length > 0;
      }
      if (cropFields) {
        this.areAnyCropFieldsNotLinked = cropFields.filter((c) => !c.shipToId).length > 0;
      }
    });

    this.customer$.subscribe((customer) => {
      if (!customer || customer?.id !== this.customer?.id) {
        this.location = null;
      }

      this.customer = customer;
    });

    combineLatest([this.location$, this.customerProgress$])
      .pipe(withLatestFrom(this.store.pipe(select(UserSelectors.selectLocations), takeUntil(this.destroyed$))))
      .subscribe(([[location, customerProgress], availableLocations]) => {
        if (location) {
          this.location = location;
        } else if (this.customer) {
          if (!this.location && availableLocations.length === 1) {
            this.location = availableLocations[0];
          }

          if (this.location) {
            this.store.dispatch(
              UserActions.relationshipSelected({
                relatedType: UserRelationshipType.Location,
                customerId: this.customer.id,
                locationId: this.location.id,
              }),
            );
          }
        }

        this.buttonItems = this.getButtonItems(!!this.location, customerProgress);
      });

    this.getDataFromStore();
    this.getCropAids();
    this.getPackages();
  }

  getDataFromStore(): void {
    this.preferredDeliveryPeriods$
      .pipe(
        combineLatestWith(
          this.store.pipe(select(OverviewSelectors.selectSalesOrderPeriods)),
          this.store.pipe(select(OverviewSelectors.selectPeriodSystem)),
          this.store.pipe(select(OverviewSelectors.selectOrderplanV2Display)),
        ),
        filter((values) => this.isDifferent(values)),
        takeUntil(this.destroyed$),
        debounceTime(100),
      )
      .subscribe(([periods, salesOrderPeriods, periodsSystem, orderPlanV2Display]) => {
        this.hasOrderPlan = orderPlanV2Display?.length > 0;
        if (!periods?.periods?.length || !salesOrderPeriods || !orderPlanV2Display?.length) {
          return;
        }

        this.setColumnsToDisplay(periods, salesOrderPeriods, periodsSystem, orderPlanV2Display);

        this.periods = periods;
        this.salesOrderPeriods = salesOrderPeriods;
        this.periodsSystem = periodsSystem;
        this.orderPlanV2Display = orderPlanV2Display;
      });
  }

  setColumnsToDisplay(
    periods: PreferredDeliveryPeriods,
    salesOrderPeriods: SalesOrderPeriod[],
    periodsSystem: PeriodSystem,
    orderPlanV2Display: OrderPlanOverviewV2[],
  ): void {
    const tempColumnToDisplay = [...this.columnsToDisplay];
    const tempDynamicPeriodsColumns = [...this.dynamicPeriodsColumns];

    this.columnsToDisplay = [];
    this.dynamicPeriodsColumns = [];

    this.columnsToDisplay.push(OrderplanColumnTypes.cropAidName);
    const periodsWithValues = this.getPeriodsWithValues(orderPlanV2Display);
    const periodsToDisplay = periods.periods.filter((x) => periodsWithValues.includes(x));
    if (this.dynamicPeriodsColumns.length === 0) {
      for (const period of periodsToDisplay) {
        const salesOrderPeriod = salesOrderPeriods.find((sop) => sop.id === period && sop.periodType === periodsSystem);
        if (salesOrderPeriod) {
          const salesOrderPeriodColumnType =
            periodsSystem === PeriodSystem.GBM
              ? deliveryPeriodsToColumnTypesForGBM[salesOrderPeriod.periodNumber]
              : OverviewModels.deliveryPeriodsToColumnTypesForHorticulture[salesOrderPeriod.periodNumber];

          this.dynamicPeriodsColumns.push({
            columnDef: salesOrderPeriodColumnType,
            headerDef: salesOrderPeriod.name,
          });

          this.columnsToDisplay.push(salesOrderPeriodColumnType);
        }
      }
    }

    this.columnsToDisplay.push(OrderplanColumnTypes.requisiteAmount);
    this.columnsToDisplay.push(OrderplanColumnTypes.customerProductInventoryAmount);
    this.columnsToDisplay.push(OrderplanColumnTypes.orderLinesTotalAmount);
    this.columnsToDisplay.push(OrderplanColumnTypes.result);

    if (
      JSON.stringify(tempColumnToDisplay) !== JSON.stringify(this.columnsToDisplay) ||
      JSON.stringify(tempDynamicPeriodsColumns) !== JSON.stringify(this.dynamicPeriodsColumns)
    ) {
      this.isTableVisible$.next(false);
    }
    setTimeout(() => {
      this.isTableVisible$.next(true);
    }, 100);
  }

  getPeriodsWithValues(orderPlanV2Display: OverviewModels.OrderPlanOverviewV2[]): number[] {
    const columnsWithValues = [];
    orderPlanV2Display.forEach((orderPlan) => {
      orderPlan.orderplanPeriods.forEach((period) => {
        if (period && period.packageCount && period.packageCount > 0) {
          if (!columnsWithValues.includes(period.periodId)) {
            columnsWithValues.push(period.periodId);
          }
        }
      });
    });
    return columnsWithValues;
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  buttonAction(data: string) {
    switch (data) {
      case ButtonAction.editCustomerProductInventory:
        this.editCorrections();
        break;
      case ButtonAction.getStockFromTelersdashboard:
        this.getStockFromTelersdashboard();
        break;
      case ButtonAction.optimizeOrderplan:
        this.optimizeOrderplan();
        break;
      case ButtonAction.createOrderReport:
        this.createOrderReport();
        break;
      case ButtonAction.createCompleteReport:
        this.createCompleteReport();
        break;
      case ButtonAction.preferedDeliveryPeriods:
        this.editPreferedDeliveryPeriods();
        break;
      case ButtonAction.numberOfPeriods:
        this.editNumberOfPeriods();
        break;
      case ButtonAction.linkCropFields:
        this.linkCropFields();
        break;
      case ButtonAction.customerProgressOrderplan:
        this.customerProgressOrderplanToggle();
        break;
      case ButtonAction.digitalProducts:
        this.openDigitalProductsDialog();
        break;
    }
  }

  doEmptyAction() {
    if (this.areAnyCropFieldsLinked) {
      this.openFieldAdvice();
    } else {
      this.linkCropFields();
    }
  }

  openFieldAdvice() {
    this.store.dispatch(OverviewActions.openFieldAdvice());
  }

  addOrderLine(cropAidId: number = null, periodId: number = null) {
    this.store.dispatch(OverviewActions.editOrderLine({ orderLine: this.getEmptyOrderLine(periodId), cropAidId }));
  }

  openEditOrderLineDialog(orderLine: OverviewModels.OrderLine, cropAidId: number) {
    this.store.dispatch(OverviewActions.editOrderLine({ orderLine, cropAidId }));
  }

  editCorrections() {
    this.store.dispatch(OverviewActions.editCustomerProductInventoryForOverviewV2());
  }

  getStockFromTelersdashboard() {
    this.store.dispatch(OverviewActions.getStockFromTelersdashboard());
  }

  optimizeOrderplan() {
    this.store.dispatch(OverviewActions.optimizeOrderPlan());
  }

  openDigitalProductsDialog() {
    this.store.dispatch(OverviewActions.openDigitalProductsDialog());
  }

  createOrderReport() {
    this.store.dispatch(OverviewActions.createOrderReport());
  }

  createCompleteReport() {
    this.store.dispatch(OverviewActions.createCompleteReport());
  }

  editPreferedDeliveryPeriods() {
    this.store.dispatch(OverviewActions.editPreferredDeliveryPeriods());
  }

  editNumberOfPeriods() {
    this.store.dispatch(OverviewActions.editNumberOfPeriods());
  }

  linkCropFields() {
    this.store.dispatch(OverviewActions.linkCropFields({ cropFieldLinkType: CropFieldLinkTypesEnum.presales }));
  }

  confirmOrder() {
    this.store.dispatch(OverviewActions.confirmOrderForOverviewV2());
  }

  customerProgressOrderplanToggle() {
    this.store.dispatch(OverviewActions.toggleCustomerProgressOrderplan());
  }

  saveCropAidInventory(data: OverviewModels.ProductInventoryData) {
    const currentInventory = this.inventories.find((x) => x.cropAidId === data.cropAidId);
    currentInventory.amount = data.amount;
    this.store.dispatch(OverviewActions.saveCustomerProductInventory({ productInventories: this.inventories }));
  }

  cropAidInventoryChanged(amount: number, cropAidId: number) {
    if (amount !== null && amount !== undefined) {
      this.productInventoryChanged.next({ amount, cropAidId });
    }
  }

  selectionChanged(data: OrderPlanOverviewV2[]): void {
    this.selectedRows = data;
  }

  private isDifferent(values: [PreferredDeliveryPeriods, SalesOrderPeriod[], PeriodSystem, OrderPlanOverviewV2[]]): boolean {
    return (
      values[0] !== this.periods ||
      values[2] !== this.periodsSystem ||
      !this.areArraysEqual(values[1], this.salesOrderPeriods) ||
      !this.areArraysEqual(values[3], this.orderPlanV2Display)
    );
  }

  private areArraysEqual<T>(array1: T[], array2: T[]): boolean {
    if (array1.length !== array2.length) {
      return false;
    }

    return array1.every((item, index) => item === array2[index]);
  }

  private getButtonItems = (hasLocation: boolean, customerProgress: CustomerProgress | undefined): ButtonItem[] => {
    const buttonItems: ButtonItem[] = [
      {
        icon: '',
        svgIcon: 'download',
        viewValue: 'getStockFromTelersdashboard',
        key: ButtonAction.getStockFromTelersdashboard,
        disabled: this.isLoading,
        tooltipText: 'getStockFromTelersdashboardTooltip',
      },
    ];

    if (hasLocation) {
      buttonItems.push(
        ...[
          {
            viewValue: 'customerProgressOrderplan',
            key: ButtonAction.customerProgressOrderplan,
            icon: customerProgress?.processes?.orderplanningOrderCompleted ? 'check_box' : 'check_box_outline_blank',
          },
          {
            icon: 'link',
            viewValue: 'linkCropFields',
            key: ButtonAction.linkCropFields,
          },
          {
            icon: '',
            svgIcon: 'stock_shelves',
            viewValue: 'editCustomerProductInventory',
            key: ButtonAction.editCustomerProductInventory,
          },
          {
            icon: 'schedule',
            viewValue: 'numberOfPeriods',
            key: ButtonAction.numberOfPeriods,
          },
          {
            icon: 'local_shipping',
            viewValue: 'preferedDeliveryPeriods',
            key: ButtonAction.preferedDeliveryPeriods,
          },
          {
            icon: 'square_foot',
            viewValue: 'optimizeOrderplan',
            key: ButtonAction.optimizeOrderplan,
          },
          {
            icon: 'apps',
            viewValue: 'digitalProducts',
            key: ButtonAction.digitalProducts,
          },
          {
            icon: 'list_alt',
            viewValue: this.switchTranslation.get('orderReport'),
            key: ButtonAction.createOrderReport,
          },
          {
            icon: 'receipt_long',
            viewValue: this.switchTranslation.get('completeReport'),
            key: ButtonAction.createCompleteReport,
          },
        ],
      );
    }

    return buttonItems;
  };

  private getEmptyOrderLine(periodId: number): OverviewModels.OrderLine {
    return {
      id: -1,
      packageId: null,
      count: null,
      year: null,
      periodId,
      status: OverviewModels.OrderLineStatus.open,
      deliveryPeriodMoment: OverviewModels.DeliveryPeriodMomentType.empty,
      treatmentType: TreatmentTypes.GBM,
      orderedDate: null,
      shipToId: 1,
      addedByOptimizer: false,
      cartId: -1,
      orderedId: -1,
      checkoutId: -1,
    };
  }

  private getCropAids() {
    this.store.pipe(takeUntil(this.destroyed$), select(OverviewSelectors.selectCropAids)).subscribe((result) => (this.cropAids = result));
  }

  private getPackages() {
    this.store.pipe(takeUntil(this.destroyed$), select(OverviewSelectors.selectPackages)).subscribe((result) => (this.packages = result));
  }
}
